import React, { useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Minus, Plus, ShoppingCart } from "lucide-react";
import publicApi from "@/lib/publicApi";
import { addToCart, cartSubtotal, getCart, storeTrackingSearch, updateCartQty } from "@/lib/storeCart";
import { PublicStoreShell } from "@/components/store/PublicStoreShell";
import { aed } from "@/lib/format";

export default function StoreCatalog() {
  const { branchCode } = useParams();
  const [searchParams] = useSearchParams();
  const qs = storeTrackingSearch(searchParams);
  const [cart, setCart] = useState(() => getCart(branchCode));
  const [category, setCategory] = useState("all");

  const { data, isLoading, error } = useQuery({
    queryKey: ["public-store", branchCode],
    queryFn: async () => {
      const { data: res } = await publicApi.get(`/public/store/${branchCode}`);
      return res;
    },
    enabled: Boolean(branchCode),
  });

  const products = data?.products ?? [];
  const categories = data?.categories ?? [];
  const filtered = useMemo(() => {
    if (category === "all") return products;
    return products.filter((p) => p.category_id === category || p.category === category);
  }, [products, category]);

  const subtotal = cartSubtotal(cart);

  function handleAdd(p) {
    const next = addToCart(branchCode, {
      menu_item_id: p.id,
      product_id: p.id,
      name: p.name,
      qty: 1,
      unit_price: p.price,
    });
    setCart([...next]);
  }

  function bump(index, delta) {
    const line = cart[index];
    const next = updateCartQty(branchCode, index, (line?.qty || 0) + delta);
    setCart([...next]);
  }

  if (isLoading) {
    return (
      <PublicStoreShell>
        <div className="flex min-h-[100dvh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin store-primary-text" />
        </div>
      </PublicStoreShell>
    );
  }

  if (error) {
    return (
      <PublicStoreShell>
        <div className="p-6 text-center text-sm text-red-600">{error.response?.data?.message || "Failed to load menu"}</div>
      </PublicStoreShell>
    );
  }

  return (
    <PublicStoreShell>
      <div className="flex min-h-[100dvh] flex-col pb-28">
        <div className="sticky top-0 z-20 border-b border-[#E8D5C8] bg-white/95 backdrop-blur px-4 py-3">
          <div className="flex items-center justify-between gap-2">
            <Link to={`/s/${branchCode}${qs}`} className="text-[13px] font-semibold text-[#F36F21]">
              ← Store
            </Link>
            <h1 className="text-[15px] font-extrabold text-[#2B0B04]">Menu</h1>
            <span className="text-[12px] text-[#8B6B5E]">{cart.length} items</span>
          </div>
          <div className="mt-3 flex gap-2 overflow-x-auto pb-1">
            <button
              type="button"
              onClick={() => setCategory("all")}
              className={`shrink-0 rounded-full px-3 py-1.5 text-[12px] font-semibold border ${
                category === "all" ? "bg-[#F36F21] text-white border-[#F36F21]" : "bg-white text-[#2B0B04] border-[#E8D5C8]"
              }`}
            >
              All
            </button>
            {categories.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => setCategory(c.id)}
                className={`shrink-0 rounded-full px-3 py-1.5 text-[12px] font-semibold border ${
                  category === c.id ? "bg-[#F36F21] text-white border-[#F36F21]" : "bg-white text-[#2B0B04] border-[#E8D5C8]"
                }`}
              >
                {c.name}
              </button>
            ))}
          </div>
        </div>

        <div className="px-3 py-3 space-y-2.5">
          {filtered.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#E8D5C8] p-8 text-center text-sm text-[#8B6B5E]">
              No items in the online catalog yet.
            </div>
          ) : (
            filtered.map((p) => (
              <div key={p.id} className="flex gap-3 rounded-2xl border border-[#E8D5C8] bg-white p-3 shadow-sm">
                <div className="h-16 w-16 shrink-0 rounded-xl bg-[#FFF3E8] overflow-hidden">
                  {p.image_url ? (
                    <img src={p.image_url} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="h-full w-full grid place-items-center text-[#F36F21] font-bold text-sm">
                      {(p.name || "?").slice(0, 2).toUpperCase()}
                    </div>
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-bold text-[#2B0B04] text-[14px] truncate">{p.name}</div>
                  {p.description && <div className="text-[11px] text-[#8B6B5E] line-clamp-2 mt-0.5">{p.description}</div>}
                  <div className="mt-2 flex items-center justify-between gap-2">
                    <span className="font-extrabold text-[#F36F21] text-[14px]">{aed(p.price)}</span>
                    <button
                      type="button"
                      onClick={() => handleAdd(p)}
                      className="inline-flex items-center gap-1 rounded-xl bg-[#F36F21] px-3 py-1.5 text-[12px] font-bold text-white"
                    >
                      <Plus size={14} /> Add
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {cart.length > 0 && (
          <div className="fixed bottom-0 inset-x-0 z-30">
            <div className="mx-auto max-w-md px-3 pb-3">
              <div className="rounded-2xl border border-[#E8D5C8] bg-white shadow-2xl p-3">
                <div className="max-h-28 overflow-y-auto space-y-1.5 mb-2">
                  {cart.map((line, i) => (
                    <div key={`${line.menu_item_id}-${i}`} className="flex items-center justify-between gap-2 text-[12px]">
                      <span className="truncate font-medium text-[#2B0B04]">{line.name}</span>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button type="button" className="h-6 w-6 rounded-md border grid place-items-center" onClick={() => bump(i, -1)}>
                          <Minus size={12} />
                        </button>
                        <span className="w-5 text-center font-bold">{line.qty}</span>
                        <button type="button" className="h-6 w-6 rounded-md border grid place-items-center" onClick={() => bump(i, 1)}>
                          <Plus size={12} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
                <Link
                  to={`/s/${branchCode}/checkout${qs}`}
                  className="flex h-11 w-full items-center justify-between rounded-xl bg-[#F36F21] px-4 text-white font-bold"
                >
                  <span className="inline-flex items-center gap-2">
                    <ShoppingCart size={16} /> Checkout
                  </span>
                  <span>{aed(subtotal)}</span>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </PublicStoreShell>
  );
}
